import { reviewLinkVersions } from './annotation';
import { assetReviewStatus, decisionReviewerKey } from './sharing';
import { versionLabel } from './versions';

/**
 * Overview of every review of a project (or of an asset shared for review):
 * who looked at which version and what they decided, the feedback of each
 * version laid out over its timeline, and two versions side by side.
 *
 * Rows ("sources") are the team's own review rounds and each review link;
 * columns are versions: saved versions (project_snapshot), exports shared to
 * a link without a saved version, and the live edit for team comments made
 * on the timeline itself. Everything here is pure: the web app passes the
 * rows it already subscribes to (times as ms since the epoch).
 */

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

export interface OverviewProjectIn {
  reviewStatus: string;
  reviewRound: number;
  /** The saved version the current round is about; '' = none. */
  reviewVersionId?: string;
  durationUs: number;
}

export interface OverviewSnapshotIn {
  id: string;
  number: number;
  name: string;
  /** manual | review | shared | backup */
  kind: string;
  /** Its render; '' = none. */
  jobId: string;
  createdAtMs: number;
  durationUs: number;
}

export interface OverviewJobIn {
  id: string;
  title: string;
  status: string;
  createdAtMs: number;
}

export interface OverviewLinkIn {
  id: string;
  label: string;
  jobId: string;
  versions?: string;
  revoked: boolean;
  /** When it stops working; undefined = never. */
  expiresAtMs?: number;
  hasPassword: boolean;
  allowDecisions: boolean;
  createdAtMs: number;
}

export interface OverviewCommentIn {
  id: string;
  parentId?: string | null;
  linkId?: string;
  authorUserId: string;
  authorName: string;
  body: string;
  timeUs?: number;
  endUs?: number;
  resolved: boolean;
  createdAtMs: number;
  jobId?: string;
  versionId?: string;
  drawing?: string;
  /** Asset review round (comments through a link to an asset); 0 = unknown. */
  assetRound?: number;
}

export interface OverviewDecisionIn {
  id: string;
  round: number;
  userId: string;
  name: string;
  decision: string;
  note: string;
  createdAtMs: number;
  jobId?: string;
  linkId?: string;
}

export interface ReviewOverviewInput {
  project: OverviewProjectIn;
  snapshots: readonly OverviewSnapshotIn[];
  jobs: readonly OverviewJobIn[];
  links: readonly OverviewLinkIn[];
  comments: readonly OverviewCommentIn[];
  decisions: readonly OverviewDecisionIn[];
  nowMs: number;
}

// ---------------------------------------------------------------------------
// Output
// ---------------------------------------------------------------------------

/** Key of the column for team comments made on the live timeline. */
export const LIVE_VERSION_KEY = 'live';
/** Key of the team's row. */
export const TEAM_SOURCE_ID = 'team';
const DELETED_SOURCE_ID = 'deleted';

export interface OverviewVersion {
  /** Snapshot id, `job:<id>` for an export without a saved version, or LIVE_VERSION_KEY. */
  key: string;
  /** "v3 · Client cut", "Export · Spring 30s", "Current edit". */
  label: string;
  /** "v3", "Export 1", "Live". */
  short: string;
  /** '' unless it is a saved version. */
  snapshotId: string;
  /** Renders of it, oldest first (what plays when it is compared). */
  jobIds: string[];
  createdAtMs: number;
  durationUs: number;
  /** The team review round it was put up for (0 = none). */
  teamRound: number;
  live: boolean;
}

export type SourceState = 'team' | 'active' | 'off' | 'expired' | 'deleted';

export interface OverviewSource {
  /** TEAM_SOURCE_ID, a review link id, or 'deleted' for a link that no longer exists. */
  id: string;
  kind: 'team' | 'link';
  name: string;
  state: SourceState;
  hasPassword: boolean;
  expiresAtMs?: number;
  allowDecisions: boolean;
  /** Exports on the link, oldest first. */
  jobIds: string[];
  /** Index into a palette (the team is 0, links follow in the order they were made). */
  colorIndex: number;
  /** Team: the rounds its versions cover, [first, last]; [0, 0] = none. */
  rounds: [number, number];
}

export interface OverviewThread {
  id: string;
  sourceId: string;
  versionKey: string;
  timeUs?: number;
  endUs?: number;
  resolved: boolean;
  authorName: string;
  /** Written by someone outside the organization through a link. */
  guest: boolean;
  body: string;
  replies: number;
  createdAtMs: number;
  drawing: boolean;
  /** Asset reviews: the round it belongs to (0 = none). */
  round: number;
}

export interface OverviewDecision {
  /** One decision per reviewer counts (see decisionReviewerKey). */
  reviewer: string;
  sourceId: string;
  versionKey: string;
  name: string;
  decision: 'approved' | 'changes_requested';
  note: string;
  createdAtMs: number;
}

/**
 * approved / changes: decided (any change request wins); waiting: shared and
 * approvals are expected; shared: shared without approvals (or the link is
 * off); no_decision: a team round passed without one; comments: feedback
 * without the version being put up for review; not_shared: nothing.
 */
export type ReviewCellStatus = 'approved' | 'changes' | 'waiting' | 'shared' | 'no_decision' | 'comments' | 'not_shared';

export interface OverviewCell {
  sourceId: string;
  versionKey: string;
  status: ReviewCellStatus;
  threads: number;
  open: number;
  /** The latest decision of each reviewer, newest first. */
  decisions: OverviewDecision[];
}

export interface ReviewOverview {
  versions: OverviewVersion[];
  sources: OverviewSource[];
  threads: OverviewThread[];
  decisions: OverviewDecision[];
  cells: Map<string, OverviewCell>;
  /** The newest version (not the live edit, unless that is all there is); null = nothing yet. */
  latestKey: string | null;
}

const cellKey = (sourceId: string, versionKey: string) => `${sourceId}\u0000${versionKey}`;

/** The grid cell of a source and a version. */
export function overviewCell(o: ReviewOverview, sourceId: string, versionKey: string): OverviewCell {
  return o.cells.get(cellKey(sourceId, versionKey)) ?? { sourceId, versionKey, status: 'not_shared', threads: 0, open: 0, decisions: [] };
}

/** How a cell's status reads (the team's "not shared" is "Not in review"). */
export function reviewCellLabel(status: ReviewCellStatus, kind: 'team' | 'link'): string {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'changes':
      return 'Changes';
    case 'waiting':
      return 'Waiting';
    case 'shared':
      return 'Shared';
    case 'no_decision':
      return 'No decision';
    case 'comments':
      return 'Comments';
    default:
      return kind === 'team' ? 'Not in review' : 'Not shared';
  }
}

export function linkSourceState(l: { revoked: boolean; expiresAtMs?: number }, nowMs: number): 'active' | 'off' | 'expired' {
  if (l.revoked) return 'off';
  if (l.expiresAtMs !== undefined && l.expiresAtMs < nowMs) return 'expired';
  return 'active';
}

/** Latest decision per reviewer, newest first. */
function latestPerReviewer<D extends { createdAtMs: number }>(list: readonly D[], keyOf: (d: D) => string): D[] {
  const by = new Map<string, D>();
  for (const d of list) {
    const k = keyOf(d);
    const cur = by.get(k);
    if (!cur || cur.createdAtMs <= d.createdAtMs) by.set(k, d);
  }
  return [...by.values()].sort((a, b) => b.createdAtMs - a.createdAtMs);
}

function decidedStatus(decisions: readonly { decision: string }[]): 'approved' | 'changes' | null {
  if (decisions.some((d) => d.decision === 'changes_requested')) return 'changes';
  if (decisions.some((d) => d.decision === 'approved')) return 'approved';
  return null;
}

// ---------------------------------------------------------------------------
// Building the overview
// ---------------------------------------------------------------------------

export function buildReviewOverview(input: ReviewOverviewInput): ReviewOverview {
  const { project, snapshots, links, comments, nowMs } = input;
  const jobs = new Map(input.jobs.map((j) => [j.id, j]));
  const snapById = new Map(snapshots.map((s) => [s.id, s]));

  // Which saved version a render is of: the snapshot's own render, or the
  // version a guest comment on that render recorded (an earlier render).
  const snapOfJob = new Map<string, string>();
  for (const s of snapshots) if (s.jobId) snapOfJob.set(s.jobId, s.id);
  for (const c of comments) if (c.jobId && c.versionId && snapById.has(c.versionId) && !snapOfJob.has(c.jobId)) snapOfJob.set(c.jobId, c.versionId);
  const versionOfJob = (jobId: string) => snapOfJob.get(jobId) ?? `job:${jobId}`;

  // Sources: the team, then links in the order they were made.
  const sources: OverviewSource[] = [
    { id: TEAM_SOURCE_ID, kind: 'team', name: 'Team review', state: 'team', hasPassword: false, allowDecisions: true, jobIds: [], colorIndex: 0, rounds: [0, 0] },
  ];
  const sourceById = new Map<string, OverviewSource>([[TEAM_SOURCE_ID, sources[0]]]);
  for (const l of [...links].sort((a, b) => a.createdAtMs - b.createdAtMs)) {
    const s: OverviewSource = {
      id: l.id,
      kind: 'link',
      name: l.label || 'Review link',
      state: linkSourceState(l, nowMs),
      hasPassword: l.hasPassword,
      expiresAtMs: l.expiresAtMs,
      allowDecisions: l.allowDecisions,
      jobIds: reviewLinkVersions(l),
      colorIndex: sources.length,
      rounds: [0, 0],
    };
    sources.push(s);
    sourceById.set(s.id, s);
  }
  const linkOfJob = (jobId: string) => sources.find((s) => s.kind === 'link' && s.jobIds.includes(jobId))?.id;
  /** A link id as a source (a link that was deleted becomes "Deleted link"). */
  const linkSource = (id: string | undefined): string => {
    if (id && sourceById.has(id)) return id;
    if (!sourceById.has(DELETED_SOURCE_ID)) {
      const s: OverviewSource = {
        id: DELETED_SOURCE_ID,
        kind: 'link',
        name: 'Deleted link',
        state: 'deleted',
        hasPassword: false,
        allowDecisions: false,
        jobIds: [],
        colorIndex: sources.length,
        rounds: [0, 0],
      };
      sources.push(s);
      sourceById.set(s.id, s);
    }
    return DELETED_SOURCE_ID;
  };

  // Team rounds: each request saves a 'review' version. The current round's
  // is on the project; earlier ones count back from it in time order.
  const reviewSnaps = snapshots.filter((s) => s.kind === 'review' || s.id === project.reviewVersionId).sort((a, b) => a.createdAtMs - b.createdAtMs);
  const roundOf = new Map<string, number>();
  const currentIdx = project.reviewVersionId ? reviewSnaps.findIndex((s) => s.id === project.reviewVersionId) : -1;
  reviewSnaps.forEach((s, i) => {
    const r = currentIdx >= 0 ? project.reviewRound - (currentIdx - i) : i + 1;
    if (r > 0 && (currentIdx < 0 || i <= currentIdx)) roundOf.set(s.id, r);
  });
  const teamVersionOf = (d: OverviewDecisionIn): string => {
    if (d.round === project.reviewRound && project.reviewVersionId && snapById.has(project.reviewVersionId)) return project.reviewVersionId;
    const before = (list: readonly OverviewSnapshotIn[]) => list.filter((s) => s.createdAtMs <= d.createdAtMs).sort((a, b) => b.createdAtMs - a.createdAtMs)[0];
    return (before(reviewSnaps) ?? before(snapshots.filter((s) => s.number > 0)))?.id ?? LIVE_VERSION_KEY;
  };

  // Decisions.
  const decisions: OverviewDecision[] = [];
  for (const d of input.decisions) {
    if (d.decision !== 'approved' && d.decision !== 'changes_requested') continue;
    const guest = !!d.linkId || (d.userId === '' && !!d.jobId);
    let sourceId: string;
    let versionKey: string;
    if (guest) {
      sourceId = linkSource(d.linkId || (d.jobId ? linkOfJob(d.jobId) : undefined));
      const jobId = d.jobId || sourceById.get(sourceId)?.jobIds[0];
      if (!jobId) continue;
      versionKey = versionOfJob(jobId);
    } else {
      sourceId = TEAM_SOURCE_ID;
      versionKey = teamVersionOf(d);
    }
    decisions.push({
      reviewer: decisionReviewerKey({ userId: guest ? '' : d.userId, name: d.name }),
      sourceId,
      versionKey,
      name: d.name,
      decision: d.decision,
      note: d.note,
      createdAtMs: d.createdAtMs,
    });
  }

  // Threads (root comments) with their reply counts.
  const byId = new Map(comments.map((c) => [c.id, c]));
  const replies = new Map<string, number>();
  for (const c of comments) if (c.parentId && byId.has(c.parentId)) replies.set(c.parentId, (replies.get(c.parentId) ?? 0) + 1);
  const threads: OverviewThread[] = [];
  for (const c of comments) {
    if (c.parentId && byId.has(c.parentId)) continue;
    const legacyGuest = !c.linkId && c.authorUserId === '' && !!c.jobId;
    const sourceId = c.linkId ? linkSource(c.linkId) : legacyGuest ? linkSource(linkOfJob(c.jobId!)) : TEAM_SOURCE_ID;
    let versionKey: string;
    if (c.versionId && snapById.has(c.versionId)) versionKey = c.versionId;
    else if (c.jobId) versionKey = versionOfJob(c.jobId);
    else if (sourceId !== TEAM_SOURCE_ID && sourceById.get(sourceId)!.jobIds.length) {
      // A member writing in a link's conversation: the version the link showed then.
      const on = sourceById.get(sourceId)!.jobIds;
      const shown = on.filter((j) => (jobs.get(j)?.createdAtMs ?? 0) <= c.createdAtMs);
      versionKey = versionOfJob(shown[shown.length - 1] ?? on[on.length - 1]);
    } else versionKey = LIVE_VERSION_KEY;
    threads.push({
      id: c.id,
      sourceId,
      versionKey,
      timeUs: c.timeUs,
      endUs: c.endUs !== undefined && c.timeUs !== undefined && c.endUs > c.timeUs ? c.endUs : undefined,
      resolved: c.resolved,
      authorName: c.authorName,
      guest: c.authorUserId === '',
      body: c.body,
      replies: replies.get(c.id) ?? 0,
      createdAtMs: c.createdAtMs,
      drawing: !!c.drawing,
      round: 0,
    });
  }

  // Versions: everything shared, put up for review or commented on.
  const keys = new Set<string>();
  for (const s of sources) for (const j of s.jobIds) keys.add(versionOfJob(j));
  for (const s of reviewSnaps) if (roundOf.has(s.id)) keys.add(s.id);
  for (const t of threads) keys.add(t.versionKey);
  for (const d of decisions) keys.add(d.versionKey);
  const jobsOf = new Map<string, string[]>();
  const allJobs = new Set<string>([...sources.flatMap((s) => s.jobIds), ...snapshots.map((s) => s.jobId).filter(Boolean), ...comments.map((c) => c.jobId ?? '').filter(Boolean)]);
  for (const j of allJobs) {
    const k = versionOfJob(j);
    jobsOf.set(k, [...(jobsOf.get(k) ?? []), j]);
  }
  const byJobAge = (a: string, b: string) => (jobs.get(a)?.createdAtMs ?? 0) - (jobs.get(b)?.createdAtMs ?? 0);
  const maxEnd = (key: string) => Math.max(0, ...threads.filter((t) => t.versionKey === key).map((t) => t.endUs ?? t.timeUs ?? 0));
  const versions: OverviewVersion[] = [];
  for (const key of keys) {
    const snap = snapById.get(key);
    const jobIds = (jobsOf.get(key) ?? []).sort(byJobAge);
    if (snap) {
      versions.push({
        key,
        label: versionLabel(snap),
        short: snap.number ? `v${snap.number}` : snap.name || 'Saved version',
        snapshotId: snap.id,
        jobIds,
        createdAtMs: snap.createdAtMs,
        durationUs: snap.durationUs,
        teamRound: roundOf.get(snap.id) ?? 0,
        live: false,
      });
    } else if (key === LIVE_VERSION_KEY) {
      versions.push({
        key,
        label: 'Current edit',
        short: 'Live',
        snapshotId: '',
        jobIds: [],
        createdAtMs: Number.POSITIVE_INFINITY,
        durationUs: project.durationUs,
        teamRound: 0,
        live: true,
      });
    } else {
      const job = jobs.get(key.slice(4));
      versions.push({
        key,
        label: `Export · ${job?.title ?? 'deleted'}`,
        short: 'Export',
        snapshotId: '',
        jobIds: jobIds.length ? jobIds : [key.slice(4)],
        // A deleted export: when its feedback came in.
        createdAtMs:
          job?.createdAtMs ??
          Math.min(...threads.filter((t) => t.versionKey === key).map((t) => t.createdAtMs), ...decisions.filter((d) => d.versionKey === key).map((d) => d.createdAtMs)),
        durationUs: project.durationUs,
        teamRound: 0,
        live: false,
      });
    }
  }
  versions.sort((a, b) => a.createdAtMs - b.createdAtMs || a.key.localeCompare(b.key));
  let exportNo = 0;
  for (const v of versions) {
    if (v.short === 'Export') v.short = `Export ${++exportNo}`;
    // Comments past the recorded length (or no length at all) still fit.
    const end = maxEnd(v.key);
    if (!(v.durationUs > 0) || end > v.durationUs) v.durationUs = Math.max(end * 1.05, end + 500_000, 1_000_000);
  }
  const teamRounds = versions.map((v) => v.teamRound).filter((r) => r > 0);
  if (teamRounds.length) sources[0].rounds = [Math.min(...teamRounds), Math.max(...teamRounds)];

  // Cells.
  const cells = new Map<string, OverviewCell>();
  for (const s of sources) {
    for (const v of versions) {
      const ts = threads.filter((t) => t.sourceId === s.id && t.versionKey === v.key);
      const ds = latestPerReviewer(
        decisions.filter((d) => d.sourceId === s.id && d.versionKey === v.key),
        (d) => d.reviewer,
      );
      let status: ReviewCellStatus = decidedStatus(ds) ?? 'not_shared';
      if (status === 'not_shared') {
        if (s.kind === 'team') {
          if (v.teamRound) status = v.snapshotId === project.reviewVersionId && project.reviewStatus === 'in_review' ? 'waiting' : 'no_decision';
        } else if (v.jobIds.some((j) => s.jobIds.includes(j))) {
          status = s.allowDecisions && s.state === 'active' ? 'waiting' : 'shared';
        }
        if (status === 'not_shared' && ts.length) status = 'comments';
      }
      cells.set(cellKey(s.id, v.key), { sourceId: s.id, versionKey: v.key, status, threads: ts.length, open: ts.filter((t) => !t.resolved).length, decisions: ds });
    }
  }
  const notLive = versions.filter((v) => !v.live);
  const latestKey = (notLive[notLive.length - 1] ?? versions[versions.length - 1])?.key ?? null;
  return { versions, sources, threads, decisions, cells, latestKey };
}

// ---------------------------------------------------------------------------
// Summary, feed, lanes, hot spots
// ---------------------------------------------------------------------------

export interface ReviewSummary {
  activeLinks: OverviewSource[];
  /** The newest version on a link or in a team round; null = none shared yet. */
  latestShared: OverviewVersion | null;
  /** Sources it went to (links and/or the team). */
  latestSharedTo: OverviewSource[];
  openThreads: number;
  /** Sources with open threads. */
  openSources: number;
  /** Decisions on latestShared, one per source it went to. */
  latestDecisions: { approved: number; changes: number; waiting: number };
}

export function reviewSummary(o: ReviewOverview): ReviewSummary {
  const sharedTo = (v: OverviewVersion) => o.sources.filter((s) => (s.kind === 'team' ? v.teamRound > 0 : v.jobIds.some((j) => s.jobIds.includes(j))));
  const latestShared = [...o.versions].reverse().find((v) => sharedTo(v).length > 0) ?? null;
  const to = latestShared ? sharedTo(latestShared) : [];
  const counts = { approved: 0, changes: 0, waiting: 0 };
  for (const s of to) {
    const st = overviewCell(o, s.id, latestShared!.key).status;
    if (st === 'approved') counts.approved++;
    else if (st === 'changes') counts.changes++;
    else counts.waiting++;
  }
  const open = o.threads.filter((t) => !t.resolved);
  return {
    activeLinks: o.sources.filter((s) => s.state === 'active'),
    latestShared,
    latestSharedTo: to,
    openThreads: open.length,
    openSources: new Set(open.map((t) => t.sourceId)).size,
    latestDecisions: counts,
  };
}

/** The version to show first: the newest one with feedback, else the newest. */
export function defaultOverviewVersion(o: ReviewOverview): string | null {
  const withFeedback = o.versions.filter((v) => !v.live && (o.threads.some((t) => t.versionKey === v.key) || o.decisions.some((d) => d.versionKey === v.key)));
  return withFeedback[withFeedback.length - 1]?.key ?? o.latestKey;
}

export type FeedState = 'open' | 'resolved' | 'all';

/** Sorts threads by timecode (untimed ones last), then by when they were written. */
export function byTimecode(a: OverviewThread, b: OverviewThread): number {
  return (a.timeUs ?? Number.POSITIVE_INFINITY) - (b.timeUs ?? Number.POSITIVE_INFINITY) || a.createdAtMs - b.createdAtMs;
}

/** Threads for the feed: one version and/or one source (null = any), open/resolved/all, by timecode. */
export function overviewFeed(o: Pick<ReviewOverview, 'threads'>, f: { versionKey?: string | null; sourceId?: string | null; state?: FeedState }): OverviewThread[] {
  const state = f.state ?? 'all';
  return o.threads
    .filter((t) => (f.versionKey == null || t.versionKey === f.versionKey) && (f.sourceId == null || t.sourceId === f.sourceId))
    .filter((t) => state === 'all' || (state === 'open' ? !t.resolved : t.resolved))
    .sort(byTimecode);
}

export interface LaneMark {
  threadId: string;
  /** Position over the duration, 0…1. */
  start: number;
  /** Range comments: where the range ends, 0…1. */
  end?: number;
  resolved: boolean;
}

export interface FeedbackLane {
  sourceId: string;
  marks: LaneMark[];
}

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));

/** Marks of timed threads over a duration. */
export function laneMarks(threads: readonly OverviewThread[], durationUs: number): LaneMark[] {
  const d = durationUs > 0 ? durationUs : 1;
  return threads
    .filter((t) => t.timeUs !== undefined)
    .sort(byTimecode)
    .map((t) => ({ threadId: t.id, start: clamp01(t.timeUs! / d), ...(t.endUs !== undefined ? { end: clamp01(t.endUs / d) } : {}), resolved: t.resolved }));
}

/**
 * One lane per source for a version: the sources it was shared with or that
 * left feedback on it, with their timed comments as marks.
 */
export function feedbackLanes(o: ReviewOverview, versionKey: string): { durationUs: number; lanes: FeedbackLane[] } {
  const v = o.versions.find((x) => x.key === versionKey);
  const durationUs = v?.durationUs ?? 1_000_000;
  const lanes: FeedbackLane[] = [];
  for (const s of o.sources) {
    const cell = overviewCell(o, s.id, versionKey);
    if (cell.status === 'not_shared' && !cell.threads) continue;
    lanes.push({
      sourceId: s.id,
      marks: laneMarks(
        o.threads.filter((t) => t.sourceId === s.id && t.versionKey === versionKey),
        durationUs,
      ),
    });
  }
  return { durationUs, lanes };
}

export interface HotSpot {
  startUs: number;
  endUs: number;
  /** Most comments overlapping one stretch of it. */
  count: number;
  /** Different sources commenting there. */
  sources: number;
}

/**
 * Where comments cluster: the duration is cut into `bins` stretches (at least
 * `minBinUs` long); stretches touched by `min` or more comments (a range
 * comment touches every stretch it covers) are hot, and neighbours merge.
 */
export function hotSpots(
  items: readonly { timeUs?: number; endUs?: number; sourceId: string }[],
  durationUs: number,
  opts: { bins?: number; min?: number; minBinUs?: number } = {},
): HotSpot[] {
  const bins = opts.bins ?? 24;
  const min = opts.min ?? 2;
  const w = Math.max(durationUs / bins, opts.minBinUs ?? 250_000);
  const n = Math.max(1, Math.ceil(durationUs / w));
  const per: { count: number; sources: Set<string> }[] = Array.from({ length: n }, () => ({ count: 0, sources: new Set<string>() }));
  for (const it of items) {
    if (it.timeUs === undefined) continue;
    const a = Math.min(n - 1, Math.max(0, Math.floor(it.timeUs / w)));
    const b = Math.min(n - 1, Math.max(a, Math.floor(Math.max(it.timeUs, (it.endUs ?? it.timeUs) - 1) / w)));
    for (let i = a; i <= b; i++) {
      per[i].count++;
      per[i].sources.add(it.sourceId);
    }
  }
  const out: HotSpot[] = [];
  let cur: { start: number; end: number; count: number; sources: Set<string> } | null = null;
  for (let i = 0; i < n; i++) {
    const p = per[i];
    if (p.count >= min) {
      if (cur && cur.end === i) {
        cur.end = i + 1;
        cur.count = Math.max(cur.count, p.count);
        for (const s of p.sources) cur.sources.add(s);
      } else {
        if (cur) out.push({ startUs: cur.start * w, endUs: Math.min(durationUs, cur.end * w), count: cur.count, sources: cur.sources.size });
        cur = { start: i, end: i + 1, count: p.count, sources: new Set(p.sources) };
      }
    }
  }
  if (cur) out.push({ startUs: cur.start * w, endUs: Math.min(durationUs, cur.end * w), count: cur.count, sources: cur.sources.size });
  return out;
}

// ---------------------------------------------------------------------------
// Comparing two versions
// ---------------------------------------------------------------------------

/** The two versions to compare first: the newest two with feedback, else the newest two. */
export function defaultComparePair(o: ReviewOverview): [string, string] | null {
  const real = o.versions.filter((v) => !v.live);
  const fed = real.filter((v) => o.threads.some((t) => t.versionKey === v.key) || o.decisions.some((d) => d.versionKey === v.key));
  const pick = fed.length >= 2 ? fed : real;
  if (pick.length < 2) return null;
  return [pick[pick.length - 2].key, pick[pick.length - 1].key];
}

export interface CompareSide {
  version: OverviewVersion;
  threads: OverviewThread[];
  /** Marks on the shared timeline (0…1 of CompareResult.durationUs). */
  marks: LaneMark[];
}

export interface CompareMatch {
  aId: string;
  bId: string;
  timeUs: number;
}

export interface CompareResult {
  /** The longer of the two: both are drawn on this scale so the same timecode lines up. */
  durationUs: number;
  a: CompareSide;
  b: CompareSide;
  /** Moments flagged in both versions (within the tolerance), each comment used once. */
  matches: CompareMatch[];
}

export function compareVersions(o: ReviewOverview, aKey: string, bKey: string, opts: { sourceId?: string | null; toleranceUs?: number } = {}): CompareResult | null {
  const va = o.versions.find((v) => v.key === aKey);
  const vb = o.versions.find((v) => v.key === bKey);
  if (!va || !vb) return null;
  const durationUs = Math.max(va.durationUs, vb.durationUs);
  const ta = overviewFeed(o, { versionKey: aKey, sourceId: opts.sourceId ?? null });
  const tb = overviewFeed(o, { versionKey: bKey, sourceId: opts.sourceId ?? null });
  const tol = opts.toleranceUs ?? 1_000_000;
  const used = new Set<string>();
  const matches: CompareMatch[] = [];
  for (const b of tb) {
    if (b.timeUs === undefined) continue;
    let best: OverviewThread | null = null;
    for (const a of ta) {
      if (a.timeUs === undefined || used.has(a.id)) continue;
      const d = Math.abs(a.timeUs - b.timeUs);
      if (d <= tol && (!best || d < Math.abs(best.timeUs! - b.timeUs))) best = a;
    }
    if (best) {
      used.add(best.id);
      matches.push({ aId: best.id, bId: b.id, timeUs: (best.timeUs! + b.timeUs) / 2 });
    }
  }
  return {
    durationUs,
    a: { version: va, threads: ta, marks: laneMarks(ta, durationUs) },
    b: { version: vb, threads: tb, marks: laneMarks(tb, durationUs) },
    matches,
  };
}

/**
 * Synced playback: where the follower should jump to so it shows the same
 * moment as the leader (seconds), or null when it is close enough already.
 * Past the follower's end it holds its last frame.
 */
export function compareSyncSeek(leaderSec: number, followerSec: number, followerDurationSec: number, toleranceSec = 0.12): number | null {
  const target = Number.isFinite(followerDurationSec) && followerDurationSec > 0 ? Math.min(leaderSec, Math.max(0, followerDurationSec - 0.04)) : leaderSec;
  return Math.abs(target - followerSec) > toleranceSec ? Math.max(0, target) : null;
}

// ---------------------------------------------------------------------------
// Assets shared for review
// ---------------------------------------------------------------------------

export interface AssetOverviewInput {
  links: readonly Pick<OverviewLinkIn, 'id' | 'label' | 'revoked' | 'expiresAtMs' | 'allowDecisions' | 'createdAtMs'>[];
  /** Comments through the asset's links. */
  comments: readonly OverviewCommentIn[];
  /** The asset's decisions (review_decision.assetId). */
  decisions: readonly OverviewDecisionIn[];
  /** asset_review; null = never reviewed. */
  review: { round: number; status: string } | null;
  durationUs: number;
  nowMs: number;
}

export type AssetRoundResult = 'approved' | 'changes_requested' | 'in_review' | 'no_decision' | 'ended';

export interface AssetRoundSource {
  sourceId: string;
  status: 'approved' | 'changes' | 'waiting';
  names: string[];
}

export interface AssetRound {
  round: number;
  current: boolean;
  result: AssetRoundResult;
  /** First and last activity (decisions and comments); undefined = none yet. */
  fromMs?: number;
  toMs?: number;
  /** One entry per link (and the team) that decided, or is still expected to. */
  sources: AssetRoundSource[];
  threads: number;
}

export interface AssetOverview {
  sources: OverviewSource[];
  rounds: AssetRound[];
  threads: OverviewThread[];
  currentRound: number;
  durationUs: number;
}

/**
 * An asset's review across its links: rounds with each link's decision, and
 * every link's comments with the round they belong to. Comments record their
 * round; older ones (0) go to the first round still deciding when they were
 * written (the round whose last decision came after them), else the current.
 */
export function buildAssetOverview(input: AssetOverviewInput): AssetOverview {
  const { review, nowMs } = input;
  const current = review?.round ?? 0;
  const sources: OverviewSource[] = [];
  for (const l of [...input.links].sort((a, b) => a.createdAtMs - b.createdAtMs)) {
    sources.push({
      id: l.id,
      kind: 'link',
      name: l.label || 'Review link',
      state: linkSourceState(l, nowMs),
      hasPassword: false,
      expiresAtMs: l.expiresAtMs,
      allowDecisions: l.allowDecisions,
      jobIds: [],
      colorIndex: sources.length + 1,
      rounds: [0, 0],
    });
  }
  const known = new Set(sources.map((s) => s.id));
  const sourceOfLink = (id: string | undefined) => {
    if (!id) return TEAM_SOURCE_ID;
    if (known.has(id)) return id;
    if (!known.has(DELETED_SOURCE_ID)) {
      known.add(DELETED_SOURCE_ID);
      sources.push({
        id: DELETED_SOURCE_ID,
        kind: 'link',
        name: 'Deleted link',
        state: 'deleted',
        hasPassword: false,
        allowDecisions: false,
        jobIds: [],
        colorIndex: sources.length + 1,
        rounds: [0, 0],
      });
    }
    return DELETED_SOURCE_ID;
  };
  const decisions = input.decisions.filter((d) => d.decision === 'approved' || d.decision === 'changes_requested');
  if (decisions.some((d) => !d.linkId))
    sources.unshift({ id: TEAM_SOURCE_ID, kind: 'team', name: 'Team', state: 'team', hasPassword: false, allowDecisions: true, jobIds: [], colorIndex: 0, rounds: [0, 0] });

  const lastDecision = new Map<number, number>();
  for (const d of decisions) lastDecision.set(d.round, Math.max(lastDecision.get(d.round) ?? 0, d.createdAtMs));
  const roundOfTime = (ms: number) => {
    for (let r = 1; r <= current; r++) if ((lastDecision.get(r) ?? -1) >= ms) return r;
    return current;
  };

  const byId = new Map(input.comments.map((c) => [c.id, c]));
  const replies = new Map<string, number>();
  for (const c of input.comments) if (c.parentId && byId.has(c.parentId)) replies.set(c.parentId, (replies.get(c.parentId) ?? 0) + 1);
  const threads: OverviewThread[] = [];
  for (const c of input.comments) {
    if (c.parentId && byId.has(c.parentId)) continue;
    const round = c.assetRound && c.assetRound > 0 ? c.assetRound : roundOfTime(c.createdAtMs);
    threads.push({
      id: c.id,
      sourceId: sourceOfLink(c.linkId),
      versionKey: String(round),
      timeUs: c.timeUs,
      endUs: c.endUs !== undefined && c.timeUs !== undefined && c.endUs > c.timeUs ? c.endUs : undefined,
      resolved: c.resolved,
      authorName: c.authorName,
      guest: c.authorUserId === '',
      body: c.body,
      replies: replies.get(c.id) ?? 0,
      createdAtMs: c.createdAtMs,
      drawing: !!c.drawing,
      round,
    });
  }

  const rounds: AssetRound[] = [];
  const maxRound = Math.max(current, ...decisions.map((d) => d.round), ...threads.map((t) => t.round));
  for (let r = 1; r <= maxRound; r++) {
    const ds = latestPerReviewer(
      decisions.filter((d) => d.round === r),
      (d) => decisionReviewerKey(d),
    );
    const ts = threads.filter((t) => t.round === r);
    const times = [...ds.map((d) => d.createdAtMs), ...ts.map((t) => t.createdAtMs)];
    const bySource = new Map<string, AssetRoundSource>();
    for (const d of [...ds].reverse()) {
      const id = sourceOfLink(d.linkId);
      const e = bySource.get(id) ?? { sourceId: id, status: 'approved' as const, names: [] };
      if (d.decision === 'changes_requested') e.status = 'changes';
      e.names.push(d.name);
      bySource.set(id, e);
    }
    const isCurrent = r === current;
    if (isCurrent && review && review.status !== 'editing') {
      for (const s of sources)
        if (s.kind === 'link' && s.state === 'active' && s.allowDecisions && !bySource.has(s.id)) bySource.set(s.id, { sourceId: s.id, status: 'waiting', names: [] });
    }
    const status = assetReviewStatus(r, ds) as Exclude<AssetRoundResult, 'no_decision' | 'ended'>;
    const result: AssetRoundResult =
      isCurrent && review
        ? review.status === 'editing'
          ? status === 'in_review'
            ? 'ended'
            : status
          : (review.status as AssetRoundResult)
        : status === 'in_review'
          ? 'no_decision'
          : status;
    const order = new Map(sources.map((s, i) => [s.id, i]));
    rounds.push({
      round: r,
      current: isCurrent,
      result,
      fromMs: times.length ? Math.min(...times) : undefined,
      toMs: times.length ? Math.max(...times) : undefined,
      sources: [...bySource.values()].sort((a, b) => (order.get(a.sourceId) ?? 99) - (order.get(b.sourceId) ?? 99)),
      threads: ts.length,
    });
  }
  const maxEnd = Math.max(0, ...threads.map((t) => t.endUs ?? t.timeUs ?? 0));
  const durationUs = input.durationUs > 0 && input.durationUs >= maxEnd ? input.durationUs : Math.max(maxEnd * 1.05, 1_000_000);
  return { sources, rounds, threads, currentRound: current, durationUs };
}
